import { createAsyncThunk } from '@reduxjs/toolkit'

import Axios from '../axios'
import { serialize, deserialize } from '../utility/serialization'
import { objectToFormData } from '../utility/formHelper'
import { handleAPIError } from '../utility/actionHelper'

const CERTIFICATIONS_URL = '/api/v1/admin/certifications'
const SECTIONS_URL = '/api/v1/admin/certifications/sections'
const QUIZZES_URL = '/api/v1/admin/certifications/quizzes'

const request = async (call, rejectWithValue, payload) => {
  try {
    const { data } = await call()
    return payload ?? deserialize(data)
  } catch (error) {
    return handleAPIError(error, rejectWithValue)
  }
}

export const getCertifications = createAsyncThunk(
  'certificationBuilder/getCertifications',
  (_, { rejectWithValue }) => request(() => Axios.get(CERTIFICATIONS_URL), rejectWithValue)
)

export const getCertification = createAsyncThunk(
  'certificationBuilder/getCertification',
  (id, { rejectWithValue }) => request(() => Axios.get(`${CERTIFICATIONS_URL}/${id}`), rejectWithValue)
)

export const createCertification = createAsyncThunk(
  'certificationBuilder/createCertification',
  (attributes, { rejectWithValue }) => request(() => Axios.post(CERTIFICATIONS_URL, serialize(attributes)), rejectWithValue)
)

export const updateCertification = createAsyncThunk(
  'certificationBuilder/updateCertification',
  ({ id, ...attributes }, { rejectWithValue }) => request(() => Axios.put(`${CERTIFICATIONS_URL}/${id}`, serialize(attributes)), rejectWithValue)
)

export const updateCertificationCoverImage = createAsyncThunk(
  'certificationBuilder/updateCertificationCoverImage',
  ({ id, photo }, { rejectWithValue }) => request(() => Axios.put(
    `${CERTIFICATIONS_URL}/${id}`,
    objectToFormData(photo ? { cover_image_attributes: { photo, name: photo.name } } : { cover_image_attributes: { _destroy: true } })
  ), rejectWithValue)
)

export const createSection = createAsyncThunk(
  'certificationBuilder/createSection',
  ({ certificationId, ...attributes }, { rejectWithValue }) => request(
    () => Axios.post(`${CERTIFICATIONS_URL}/${certificationId}/sections`, serialize(attributes)), rejectWithValue
  )
)

export const updateSection = createAsyncThunk(
  'certificationBuilder/updateSection',
  ({ certificationId, id, ...attributes }, { rejectWithValue }) => request(
    () => Axios.put(`${CERTIFICATIONS_URL}/${certificationId}/sections/${id}`, serialize(attributes)), rejectWithValue
  )
)

export const deleteSection = createAsyncThunk(
  'certificationBuilder/deleteSection',
  ({ certificationId, id }, { rejectWithValue }) => request(
    () => Axios.delete(`${CERTIFICATIONS_URL}/${certificationId}/sections/${id}`), rejectWithValue, id
  )
)

export const createLesson = createAsyncThunk(
  'certificationBuilder/createLesson',
  ({ sectionId, ...attributes }, { rejectWithValue }) => request(
    () => Axios.post(`${SECTIONS_URL}/${sectionId}/lessons`, serialize(attributes)), rejectWithValue
  )
)

export const updateLesson = createAsyncThunk(
  'certificationBuilder/updateLesson',
  ({ sectionId, id, ...attributes }, { rejectWithValue }) => request(
    () => Axios.put(`${SECTIONS_URL}/${sectionId}/lessons/${id}`, serialize(attributes)), rejectWithValue
  )
)

export const deleteLesson = createAsyncThunk(
  'certificationBuilder/deleteLesson',
  ({ sectionId, id }, { rejectWithValue }) => request(
    () => Axios.delete(`${SECTIONS_URL}/${sectionId}/lessons/${id}`), rejectWithValue, { sectionId, id }
  )
)

export const createQuestion = createAsyncThunk(
  'certificationBuilder/createQuestion',
  ({ quizId, ...attributes }, { rejectWithValue }) => request(
    () => Axios.post(`${QUIZZES_URL}/${quizId}/questions`, serialize(attributes)), rejectWithValue
  )
)

export const updateQuestion = createAsyncThunk(
  'certificationBuilder/updateQuestion',
  ({ quizId, id, ...attributes }, { rejectWithValue }) => request(
    () => Axios.put(`${QUIZZES_URL}/${quizId}/questions/${id}`, serialize(attributes)), rejectWithValue
  )
)

export const deleteQuestion = createAsyncThunk(
  'certificationBuilder/deleteQuestion',
  ({ quizId, id }, { rejectWithValue }) => request(() => Axios.delete(`${QUIZZES_URL}/${quizId}/questions/${id}`), rejectWithValue, { quizId, id })
)

export const searchInstructorCandidates = (search, types) => Axios.get('/api/v1/users', { params: { search, type: types } })
  .then(({ data }) => deserialize(data).users)
